import React, { useState, useEffect } from 'react';
import { validateEmailRealTime } from '../utils/validation';

const EmailInput = ({
  value = '',
  onChange,
  onValidationChange,
  placeholder = 'Enter email address',
  required = false,
  disabled = false,
  className = 'form-input',
  name = 'email',
  label,
  showValidation = true,
  allowDisposable = true,
  style = {}
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [validation, setValidation] = useState({ isValid: true, errors: [] });
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Real-time validation
    let validationResult;
    if (showValidation && newValue.trim()) {
      validationResult = validateEmailRealTime(newValue);
      setValidation(validationResult);
      
      if (onValidationChange) {
        onValidationChange(validationResult);
      }
    } else {
      validationResult = { isValid: true, errors: [] };
      setValidation(validationResult);
      if (onValidationChange) {
        onValidationChange(validationResult);
      }
    }

    if (onChange) {
      onChange(newValue, validationResult);
    }
  };

  const handleBlur = (e) => {
    setTouched(true);
    setFocused(false);
    
    // Validate on blur
    if (showValidation) {
      const validationResult = validateEmailRealTime(localValue);
      setValidation(validationResult);
      
      if (onValidationChange) {
        onValidationChange(validationResult);
      }
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const getInputClassName = () => {
    let classes = className;
    
    if (showValidation && touched && !focused) {
      if (validation.isValid && localValue.trim()) {
        classes += ' email-input-valid';
      } else if (!validation.isValid) {
        classes += ' email-input-invalid';
      }
    }
    
    return classes;
  };

  const shouldShowErrors = showValidation && touched && !focused && !validation.isValid;
  const shouldShowSuccess = showValidation && touched && !focused && validation.isValid && localValue.trim();

  return (
    <div className="email-input-container">
      {label && (
        <label className="form-label">
          {label}
          {required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <input
          type="email"
          name={name}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={getInputClassName()}
          style={style}
          autoComplete="email"
        />
        
        {/* Validation Icons */}
        {showValidation && touched && !focused && (
          <div style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '16px'
          }}>
            {validation.isValid && localValue.trim() ? (
              <span style={{ color: '#10b981' }}>✓</span>
            ) : !validation.isValid ? (
              <span style={{ color: '#ef4444' }}>✗</span>
            ) : null}
          </div>
        )}
      </div>

      {/* Error Messages */}
      {shouldShowErrors && (
        <div className="email-validation-errors">
          {validation.errors.map((error, index) => (
            <div key={index} className="email-error-message">
              <span style={{ color: '#ef4444', fontSize: '12px', marginRight: '4px' }}>⚠</span>
              <span style={{ color: '#ef4444', fontSize: '12px' }}>{error}</span>
            </div>
          ))}
          {validation.suggestion && (
            <div className="email-suggestion">
              <span style={{ color: '#f59e0b', fontSize: '12px', marginRight: '4px' }}>💡</span>
              <span style={{ color: '#f59e0b', fontSize: '12px' }}>
                Did you mean: <strong>{validation.suggestion}</strong>?
              </span>
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {shouldShowSuccess && (
        <div className="email-validation-success">
          <span style={{ color: '#10b981', fontSize: '12px', marginRight: '4px' }}>✓</span>
          <span style={{ color: '#10b981', fontSize: '12px' }}>Valid email address</span>
        </div>
      )}
    </div>
  );
};

export default EmailInput;